// src/components/AnalyticsDashboard/composables/useTimeSeriesResolution.ts
import { computed, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  BucketSize,
  bucketSizeToInterval,
  intervalToBucketSize
} from '@/api-client/models/indoorclimate/Contracts'
import { calculateBucketSizes } from '@/utils/calculateBucketSizes'
import { useTimeSelection } from '@/composables/useTimeRange' // Import the composable itself
import { useComponentFilter } from '../measurements/useMeasurements' // Import the composable itself

// Define Interval type based on the keys of bucketSizeToInterval
export type Interval = keyof typeof bucketSizeToInterval

type ResolutionButton = {
  text: string
  name: string
  onClick: () => void
}

// Define the type for the return value of useTimeSelection
type TimeSelectionState = ReturnType<typeof useTimeSelection>
// Define the type for the componentFilters ref (Ref containing the result of useComponentFilter)
// Assuming useComponentFilter returns Ref<FilterStateObject>
type ComponentFilterStateObject = ReturnType<typeof useComponentFilter>['value'] // Get the type of the object inside the Ref
type ComponentFilterRef = Ref<ComponentFilterStateObject>

const RESOLUTIONS_TEXT: Record<BucketSize, { en: string; sv: string }> = {
  MAX: { en: 'Max', sv: 'Max' },
  YEAR: { en: 'Year', sv: 'År' },
  QUARTER: { en: 'Quarter', sv: 'Kvartal' },
  MONTH: { en: 'Month', sv: 'Månad' },
  WEEK: { en: 'Week', sv: 'Vecka' },
  DAY: { en: 'Day', sv: 'Dag' },
  HOUR: { en: 'Hour', sv: 'Timme' },
  MINUTE_10: { en: '10 minutes', sv: '10 minuter' }
}

export function useTimeSeriesResolution(
  timeSelection: Readonly<TimeSelectionState>, // Pass the reactive object/store directly
  componentFilters: ComponentFilterRef, // Pass the Ref<FilterStateObject>
  onResolutionChange?: () => void
) {
  const { locale } = useI18n()
  const buttons = ref<ResolutionButton[]>([])

  const currentInterval = computed<Interval | undefined>(() => {
    // Access the value of the Ref first
    const filter = componentFilters.value.query?.groupBy?.find(
      (x) => x?.type === 'timestamp_bucket'
    )
    return filter && 'interval' in filter ? (filter.interval as Interval) : undefined
  })

  const bucketSize = computed<BucketSize>(() => {
    const interval = currentInterval.value ?? '1 day'
    return intervalToBucketSize[interval] ?? 'DAY'
  })

  const selectedName = computed<string | undefined>(() =>
    bucketSize.value && RESOLUTIONS_TEXT[bucketSize.value]
      ? RESOLUTIONS_TEXT[bucketSize.value][locale.value]
      : undefined
  )

  const availableBucketSizes = computed<BucketSize[] | undefined>(() =>
    // Access properties directly if timeSelection is reactive/store
    timeSelection.absoluteRange
      ? calculateBucketSizes(timeSelection.absoluteRange[0], timeSelection.absoluteRange[1])
      : undefined
  )

  watch(
    availableBucketSizes,
    (newSizes, oldSizes) => {
      if (!newSizes) {
        buttons.value = []
        return
      }

      const primaryNewSize = newSizes[0]
      if (
        primaryNewSize !== oldSizes?.[0] &&
        bucketSizeToInterval[primaryNewSize] !== currentInterval.value
      ) {
        const currentFilterState = componentFilters.value
        const currentGroupBy = currentFilterState.query?.groupBy ?? []
        const otherGroupBy = currentGroupBy.filter((g) => g?.type !== 'timestamp_bucket')
        componentFilters.value = {
          ...currentFilterState,
          query: {
            ...currentFilterState.query,
            groupBy: [
              ...otherGroupBy,
              { type: 'timestamp_bucket', interval: bucketSizeToInterval[primaryNewSize] }
            ]
          }
        }
      }

      buttons.value = newSizes.map((resolution) => {
        const resolutionText = RESOLUTIONS_TEXT[resolution]?.[locale.value] ?? resolution
        return {
          text: resolutionText,
          name: resolutionText,
          onClick: () => {
            // Access .value to modify the state object within the Ref
            const currentFilterState = componentFilters.value
            const currentGroupBy = currentFilterState.query?.groupBy ?? []
            const otherGroupBy = currentGroupBy.filter((g) => g?.type !== 'timestamp_bucket')
            // Assign directly to componentFilters.value
            componentFilters.value = {
              ...currentFilterState,
              query: {
                ...currentFilterState.query,
                groupBy: [
                  ...otherGroupBy,
                  { type: 'timestamp_bucket', interval: bucketSizeToInterval[resolution] }
                ]
              }
            }
            onResolutionChange?.()
          }
        }
      })
    },
    { immediate: true }
  )

  return {
    bucketSize,
    buttons,
    selectedName
  }
}
